import { useState, useEffect } from 'react';
import { useNotificationProvider } from 'hooks';
import countCheckedRow from '../libs/countCheckedRow';
import filterRoles from '../libs/filterRoles';
import handleAllChecked from '../libs/handleAllChecked';
import handleRole from '../libs/handleRole';
import makeCheckedRoles from '../libs/makeCheckedRoles';
import createRole from '../libs/createRole';
import convertRole from '../libs/convertRole';
import updateRoleApi from 'api/menus/updateRoleApi';
import { makePostRoles } from '../libs/makePostValues';
import { Type } from '../const';
import { makeEmptyRoles } from '../libs/makeEmptyValues';

/**
 * @function useRoles
 * @param { Object } props
 * @param { boolean } props.open
 * @param { object } props.state
 * @param { object } props.handler
 * @param { string } props.groupId
 * @param { array } props.selectedRoles
 * @param { array } props.roles
 * @param { function } props.onMenu
 * @param { boolean } props.trgRst
 * @returns { object }
 */
function useRoles(props) {
	const { onData, onUpdate } = props.handler;

	const initIcons = { add: true, delete: true };

	const { notifySuccess, notifyError } = useNotificationProvider();

	const [groupRoles, setGroupRoles] = useState(props.selectedRoles);
	const [filteredRoles, setFilteredRoles] = useState([]);
	const [chosenOptions, setChosenOptions] = useState([]);
	const [count, setCount] = useState(0);
	const [isDisabled, setIsDisabled] = useState(initIcons);
	const [isCheckAll, setIsCheckAll] = useState(false);
	const [loading, setLoading] = useState(false);

	const onOptions = (values) => {
		setIsDisabled((prev) => ({ ...prev, add: values.length === 0 }));
		setChosenOptions([...values]);
	};

	const onAdd = () => {
		const results = convertRole(chosenOptions, props.groupId);
		setGroupRoles([...groupRoles, ...results]);
		setChosenOptions([]);
		setIsDisabled(initIcons);
		onData(Type.Role, Type.Continue, props.groupId, [...groupRoles, ...results]);
		onUpdate(Type.Role, true);
	};

	const onDelete = () => {
		const filteredRoles = groupRoles.filter((r) => !r.checked);

		if (filteredRoles.length) {
			onData(Type.Role, Type.Continue, props.groupId, [...filteredRoles]);
		} else {
			const emptyRoles = makeEmptyRoles(props.groupId);
			onData(Type.Role, Type.Continue, props.groupId, [...emptyRoles]);
		}

		setGroupRoles([...filteredRoles]);
		setIsCheckAll(false);
		setIsDisabled(initIcons);
		onUpdate(Type.Role, true);
	};

	const onCheckAll = (status) => {
		if (groupRoles.length === 0) return;
		setIsCheckAll(status);
		const results = handleAllChecked(groupRoles, status);
		setGroupRoles([...results]);
	};

	const onCheck = (id) => {
		const results = makeCheckedRoles(groupRoles, id);
		setGroupRoles([...results]);
	};

	const onHandle = (e, id) => {
		const results = handleRole(groupRoles, id, e.target.name, e.target.value);
		setGroupRoles(results);
		onData(Type.Role, Type.Continue, props.groupId, [...results]);
		onUpdate(Type.Role, true);
	};

	const onCreate = () => {
		const results = createRole(groupRoles, props.groupId);
		setGroupRoles([...results]);
		onData(Type.Role, Type.Continue, props.groupId, [...results]);
		onUpdate(Type.Role, true);
	};

	const onSave = async () => {
		try {
			setLoading(true);
			props.onMenu(Type.Role, groupRoles, props.groupId);
			const result = makePostRoles(groupRoles, props.groupId);
			const res = await updateRoleApi(result);
			onUpdate(Type.Role, false);
			notifySuccess(res);
		} catch (error) {
			notifyError(error.message);
		} finally {
			setLoading(false);
		}
	};

	const onReset = () => {
		onCheckAll(false);
		setGroupRoles([...props.selectedRoles]);
		setIsDisabled(initIcons);
		onData(Type.Role, Type.Reset, props.groupId);
		onUpdate(Type.Role, false);
	};

	useEffect(() => {
		onReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.trgRst]);

	useEffect(() => {
		setIsDisabled((prev) => ({ ...prev, delete: count === 0 }));
	}, [count]);

	useEffect(() => {
		if (groupRoles.length === 0) {
			setFilteredRoles(props.roles);
		} else if (groupRoles.length > 0) {
			const _count = countCheckedRow(groupRoles);
			const _roles = filterRoles(props.roles, groupRoles);
			setCount(_count);
			setFilteredRoles(_roles);
		}
	}, [props.groupId, groupRoles, props.roles]);

	const state = {
		groupRoles,
		filteredRoles,
		chosenOptions,
		count,
		isDisabled,
		isCheckAll,
		loading,
	};

	const handler = {
		onOptions,
		onAdd,
		onDelete,
		onCheckAll,
		onCheck,
		onHandle,
		onCreate,
		onSave,
		onReset,
	};

	return { state, handler };
}

export default useRoles;
